import React, { useMemo, useState } from 'react';
import { BKJPagination } from '@bookjane2/bookjane-design-library';
import {
  DataTableContainer,
  StyledTable,
  TableHeaderRow,
  TableHeaderCell,
  SortArrow,
  TableRow,
  TableCell,
  PaginationWrapper,
} from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.styled';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.styled';
import {
  DataTableProps,
  SortState,
} from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.types';
import { DATA_TABLE_PAGE_SIZE } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.constants';

export const DataTable: React.FC<DataTableProps> = ({
  columns,
  rows,
  pageSize = DATA_TABLE_PAGE_SIZE,
  ariaLabel,
  emptyText = 'No data available',
  fixedLayout,
  serverPagination,
}) => {
  const [sort, setSort] = useState<SortState | null>(null);
  const [clientPage, setClientPage] = useState(1);

  const isServerPaginated = !!serverPagination;

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    return [...rows].sort((a, b) => {
      const aVal = a[sort.key];
      const bVal = b[sort.key];
      if (aVal === bVal) return 0;
      const cmp = aVal < bVal ? -1 : 1;
      return sort.direction === 'asc' ? cmp : -cmp;
    });
  }, [rows, sort]);

  const currentPage = isServerPaginated ? serverPagination.currentPage : clientPage;
  const totalPages = isServerPaginated
    ? serverPagination.totalPages
    : Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const displayRows = isServerPaginated
    ? sortedRows
    : sortedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const fillerRowCount =
    totalPages > 1 && !serverPagination?.isLoading ? Math.max(0, pageSize - displayRows.length) : 0;

  const handlePageChange = (page: number) => {
    if (isServerPaginated) {
      serverPagination.onPageChange(page);
    } else {
      setClientPage(page);
    }
  };

  const handleSort = (key: string) => {
    setSort((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
    if (!isServerPaginated) {
      setClientPage(1);
    }
  };

  if (rows.length === 0 && !serverPagination?.isLoading) {
    return <EmptyStateContainer>{emptyText}</EmptyStateContainer>;
  }

  return (
    <DataTableContainer>
      <StyledTable role="table" aria-label={ariaLabel} $fixedLayout={fixedLayout}>
        <thead>
          <TableHeaderRow>
            {columns.map((col) => (
              <TableHeaderCell
                key={col.key}
                $sortable={col.sortable}
                style={{
                  ...(col.width ? { width: col.width } : {}),
                  ...(col.align ? { textAlign: col.align } : {}),
                }}
                onClick={col.sortable ? () => handleSort(col.key) : undefined}
                aria-sort={
                  sort?.key === col.key
                    ? sort.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
              >
                {col.label}
                {col.sortable && sort?.key === col.key && (
                  <SortArrow>{sort.direction === 'asc' ? '▲' : '▼'}</SortArrow>
                )}
              </TableHeaderCell>
            ))}
          </TableHeaderRow>
        </thead>
        <tbody>
          {displayRows.map((row, idx) => (
            <TableRow key={idx} $even={idx % 2 === 1}>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  style={{
                    ...(col.align ? { textAlign: col.align } : {}),
                    ...(col.noWrap ? { whiteSpace: 'nowrap' } : {}),
                  }}
                >
                  {col.renderCell ? col.renderCell(row[col.key]) : row[col.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {Array.from({ length: fillerRowCount }).map((_, i) => (
            <TableRow key={`filler-${i}`} $filler $even={(displayRows.length + i) % 2 === 1}>
              {columns.map((col) => (
                <TableCell key={col.key}>&nbsp;</TableCell>
              ))}
            </TableRow>
          ))}
        </tbody>
      </StyledTable>
      {totalPages > 1 && (
        <PaginationWrapper>
          <BKJPagination
            currentPage={currentPage}
            pageCount={totalPages}
            onFirstPage={() => handlePageChange(1)}
            onPrevPage={() => handlePageChange(Math.max(1, currentPage - 1))}
            onNextPage={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
            onLastPage={() => handlePageChange(totalPages)}
          />
        </PaginationWrapper>
      )}
    </DataTableContainer>
  );
};
